import { type ApolloError, useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import {
  type ProfessionalQualificationData,
  type ProfessionalQualificationFormData,
} from './types';
import { checkInvalidDate } from '../checkInvalidDate';
import { type SelectOption } from '../../../../../components';

type GetProfessionalQualification = {
  professionalQualificationFormData: ProfessionalQualificationFormData;
  countryList: SelectOption[];
  loading: boolean;
  error: ApolloError | undefined;
};

export const useGetProfessionalQualificationFormData = (
  id: string,
): GetProfessionalQualification => {
  const {
    data: professionalQualificationData,
    loading: professionalQualificationDataLoading,
    error: professionalQualificationDataError,
  } = useQuery<ProfessionalQualificationData>(
    gql`
      query ($id: ID!) {
        professionalQualification(id: $id) {
          awardDate
          country
          professionField
          professionalQualification
          awardBodyName
          awardName
          attainmentMeans
          supportingDocuments {
            id
            link
            name
          }
        }
      }
    `,
    {
      variables: { id },
      skip: !id,
      fetchPolicy: 'network-only',
    },
  );

  const {
    data: countryData,
    loading: countryLoading,
    error: countryError,
  } = useQuery(
    gql`
      query {
        programmeChoiceMetadata {
          countryList {
            value
            label
          }
        }
      }
    `,
    {
      fetchPolicy: 'network-only',
    },
  );

  const countryList: SelectOption[] =
    countryData?.programmeChoiceMetadata?.countryList ?? [];
  const professionalQualification: ProfessionalQualificationFormData =
    (professionalQualificationData?.professionalQualification ??
      {}) as ProfessionalQualificationFormData;

  const {
    awardDate,
    awardName,
    country,
    professionField,
    professionalQualification: professionalQualificationField,
    awardBodyName,
    attainmentMeans,
    supportingDocuments,
  } = professionalQualification ?? {};

  const professionalQualificationFormData: ProfessionalQualificationFormData = {
    awardDate: checkInvalidDate(awardDate) ? null : awardDate,
    awardName: awardName ?? '',
    country,
    professionField,
    professionalQualification: professionalQualificationField,
    awardBodyName,
    attainmentMeans,
    supportingDocuments: supportingDocuments ?? [],
  };

  return {
    professionalQualificationFormData,
    countryList,
    loading: countryLoading || professionalQualificationDataLoading,
    error: countryError ?? professionalQualificationDataError,
  };
};
